import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getMediaUrl } from '../utils/mediaUtils';
import { 
    getMeetingDetail, 
    getMeetingParticipants, 
    addMeetingParticipants, 
    removeMeetingParticipants,
    updateMeetingAttendance, 
    blastMeetingWhatsapp, 
    exportMeetingCsv,
    markMeetingSessionFinished
} from '../services/meetingApi';
import axios from 'axios'; // For user search
import Header from '../components/layout/Header';
import NavigationButton from '../components/layout/Navigation';
import WarningModal from '../components/popup/WarningModal';
import { Helmet } from 'react-helmet';
import { formatCurrency } from '../utils/formatters';

const MeetingManagementPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();

    const [meeting, setMeeting] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [forbidden, setForbidden] = useState(false);

    // Selection
    const [selectedIds, setSelectedIds] = useState([]);

    // Add Participant Modal
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [users, setUsers] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]);

    // Blast Modal
    const [showBlastModal, setShowBlastModal] = useState(false);
    const [blastMessage, setBlastMessage] = useState('');
    const [blasting, setBlasting] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [meetRes, partRes] = await Promise.all([
                    getMeetingDetail(slug),
                    getMeetingParticipants(slug)
                ]);
                setMeeting(meetRes.data);
                setParticipants(partRes.data);
            } catch (err) {
                console.error(err);
                if (err.response?.status === 403 || err.response?.status === 401) {
                    setForbidden(true);
                } else {
                    setError('Gagal memuat data manajemen agenda internal.');
                }
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [slug]);


    // User Search
    useEffect(() => {
        if (showAddModal) {
            const delayDebounceFn = setTimeout(async () => {
                setSearching(true);
                try {
                    const user = JSON.parse(localStorage.getItem('user'));
                    const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users/`, {
                        params: { search: searchQuery },
                        headers: { Authorization: `Bearer ${user.access}` }
                    });
                    // Filter out already added participants
                    const existingUserIds = participants.map(p => p.user);
                    setUsers(res.data.results ? res.data.results.filter(u => !existingUserIds.includes(u.id)) : []);
                } catch (err) {
                    console.error(err);
                } finally {
                    setSearching(false);
                }
            }, 500);
            return () => clearTimeout(delayDebounceFn);
        }
    }, [searchQuery, showAddModal, participants]);

    if (forbidden) {
        return <WarningModal redirectPath={window.location.pathname + window.location.search} />;
    }

    const handleAddParticipants = async () => {
        if (selectedUserIds.length === 0) return;
        setLoading(true);
        try {
            await addMeetingParticipants(slug, selectedUserIds);
            const partRes = await getMeetingParticipants(slug);
            setParticipants(partRes.data);
            setShowAddModal(false);
            setSelectedUserIds([]);
        } catch (err) {
            alert('Gagal menambahkan peserta.');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveParticipants = async () => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Apakah Anda yakin ingin menghapus ${selectedIds.length} peserta terpilih dari rapat ini?`)) return;
        try {
            await removeMeetingParticipants(slug, selectedIds);
            setParticipants(prev => prev.filter(p => !selectedIds.includes(p.id)));
            setSelectedIds([]);
            alert('Peserta berhasil dihapus.');
        } catch (err) {
            alert('Gagal menghapus peserta.');
        }
    };

    const handleAttendanceChange = async (participantId, status, remarks = '', sessionId = null) => {
        try {
            await updateMeetingAttendance(slug, { 
                participant_id: participantId, 
                status, 
                remarks,
                session_id: sessionId
            });
            
            setParticipants(prev => prev.map(p => {
                if (p.id !== participantId) return p;
                
                if (sessionId) {
                    const existing = p.session_attendances || [];
                    const found = existing.find(a => a.session === sessionId);
                    let newAttendances;
                    if (found) {
                        newAttendances = existing.map(a => a.session === sessionId ? { ...a, status, remarks } : a);
                    } else {
                        newAttendances = [...existing, { session: sessionId, status, remarks }];
                    }
                    return { ...p, session_attendances: newAttendances };
                } else {
                    return { ...p, status, remarks };
                }
            }));
        } catch (err) {
            alert('Gagal memperbarui absensi.');
        }
    };

    const handleBlast = async () => {
        if (!blastMessage) return;
        setBlasting(true);
        try {
            // If selectedIds is empty, blast to all participants
            const idsToBlast = selectedIds.length > 0 ? selectedIds : null;
            await blastMeetingWhatsapp(slug, blastMessage, idsToBlast);
            alert('Blast WhatsApp berhasil dikirim!');
            setShowBlastModal(false);
            setBlastMessage('');
        } catch (err) {
            alert('Gagal mengirim blast.');
        } finally {
            setBlasting(false);
        }
    };

    const handleMarkFinished = async (sessionId) => {
        if (!window.confirm('Tandai sesi ini selesai dan kirim reminder untuk sesi berikutnya (jika ada)?')) return;
        try {
            const res = await markMeetingSessionFinished(slug, sessionId);
            // Refresh meeting data to update session status
            const meetRes = await getMeetingDetail(slug);
            setMeeting(meetRes.data);
            alert(res.data.message);
        } catch (err) {
            alert('Gagal menandai sesi selesai.');
        }
    };

    const handleExport = async () => {
        try {
            const res = await exportMeetingCsv(slug);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `peserta_rapat_${slug}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            alert('Gagal mengekspor CSV.');
        }
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === participants.length) setSelectedIds([]);
        else setSelectedIds(participants.map(p => p.id));
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    if (loading && !meeting) return <div className="min-h-screen flex items-center justify-center">Memuat...</div>;

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col pt-16 pb-20 w-full overflow-x-hidden">
            <Helmet>
                <title>Kelola Rapat: {meeting?.title} | BARAKAH APP</title>
            </Helmet>
            <Header />

            <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-8 min-w-0">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Link to="/dashboard/meetings" className="text-blue-600 hover:underline text-xs font-bold uppercase tracking-widest flex items-center gap-1">
                                <span className="material-icons text-sm">arrow_back</span>
                                Kembali
                            </Link>
                        </div>
                        <h1 className="text-2xl font-black text-gray-900 leading-tight">{meeting?.title}</h1>
                        <p className="text-sm text-gray-500 font-medium">Manajemen Peserta & Absensi Kehadiran</p>
                    </div>
                    <div className="flex flex-wrap gap-2 w-full md:w-auto">
                        <button 
                            onClick={handleExport}
                            className="flex-1 md:flex-none bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 hover:bg-gray-50 transition"
                        >
                            <span className="material-icons text-sm text-green-600">download</span>
                            EXPORT CSV
                        </button>
                        <button 
                            onClick={() => {
                                setBlastMessage(`Yth. Bapak/Ibu/Sdr/i,

Kami mengundang Anda untuk menghadiri rapat *${meeting?.title}* yang akan dilaksanakan pada:

Hari/Tgl: ${new Date(meeting?.start_date).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
Jam: ${new Date(meeting?.start_date).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })} WIB
Lokasi: ${meeting?.location}

Detail Agenda & Peserta:
{meeting_link}

Harap klik link tersebut untuk konfirmasi akan hadir (gunakan akun yang sudah didaftarkan di Barakah App)

Mohon kehadirannya tepat waktu. Terima kasih.

Pesan otomatis dari Barakah App`);
                                setShowBlastModal(true);
                            }}
                            className="flex-1 md:flex-none bg-green-600 text-white px-4 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 hover:bg-green-700 transition shadow-lg shadow-green-100"
                        >
                            <span className="material-icons text-sm">campaign</span>
                            BLAST WA ({selectedIds.length > 0 ? selectedIds.length : 'SEMUA'})
                        </button>
                        {selectedIds.length > 0 && (
                            <button 
                                onClick={handleRemoveParticipants}
                                className="flex-1 md:flex-none bg-red-600 text-white px-4 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 hover:bg-red-700 transition shadow-lg shadow-red-100"
                            >
                                <span className="material-icons text-sm">delete</span>
                                HAPUS ({selectedIds.length})
                            </button>
                        )}
                        <button 
                            onClick={() => setShowAddModal(true)}
                            className="flex-1 md:flex-none bg-blue-600 text-white px-4 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 hover:bg-blue-700 transition shadow-lg shadow-blue-100"
                        >
                            <span className="material-icons text-sm">person_add</span>
                            TAMBAH PESERTA
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-100">
                                    <th className="p-5 w-12 text-center">
                                        <input 
                                            type="checkbox" 
                                            checked={selectedIds.length === participants.length && participants.length > 0}
                                            onChange={toggleSelectAll}
                                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                    </th>
                                    <th className="p-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Peserta</th>
                                    <th className="p-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Info Kontak</th>
                                    <th className="p-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Konfirmasi Kehadiran</th>
                                    {meeting?.sessions?.map(s => (
                                        <th key={s.id} className="p-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center min-w-[150px]">
                                            <div className="mb-2">{s.title}</div>
                                            <button 
                                                onClick={() => handleMarkFinished(s.id)}
                                                disabled={s.is_finished}
                                                className={`px-3 py-1 rounded-full text-[8px] font-black transition shadow-sm ${s.is_finished ? 'bg-gray-100 text-gray-300 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                            >
                                                {s.is_finished ? 'SELESAI' : 'SELESAIKAN SESSION'}
                                            </button>
                                        </th>
                                    ))}

                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {participants.map(p => (
                                    <tr key={p.id} className="hover:bg-gray-50/50 transition">
                                        <td className="p-5 text-center">
                                            <input 
                                                type="checkbox" 
                                                checked={selectedIds.includes(p.id)}
                                                onChange={() => toggleSelect(p.id)}
                                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                        </td>
                                        <td className="p-5">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 font-black text-xs shrink-0 overflow-hidden">
                                                    {p.user_details?.profile?.profile_image ? (
                                                        <img src={getMediaUrl(p.user_details.profile.profile_image)} alt="" className="w-full h-full object-cover" />
                                                    ) : (
                                                        p.user_details?.username?.substring(0, 2).toUpperCase()
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-black text-gray-900">{p.user_details?.profile?.name_full || p.user_details?.username}</p>
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">ID: {p.user}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-5">
                                            <p className="text-xs font-bold text-gray-600">{p.user_details?.email}</p>
                                            <p className="text-[10px] text-gray-400">{p.user_details?.profile?.phone || '-'}</p>
                                        </td>
                                        <td className="p-5 text-center">
                                            {p.rsvp_status === 'attending' ? (
                                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-800 text-[10px] font-black rounded-full uppercase tracking-wider">
                                                    <span className="material-icons text-[10px]">check_circle</span>
                                                    Akan Hadir
                                                </span>
                                            ) : p.rsvp_status === 'not_attending' ? (
                                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-rose-100 text-rose-800 text-[10px] font-black rounded-full uppercase tracking-wider">
                                                    <span className="material-icons text-[10px]">cancel</span>
                                                    Tidak Hadir
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-800 text-[10px] font-black rounded-full uppercase tracking-wider">
                                                    <span className="material-icons text-[10px]">help</span>
                                                    Belum Konfirmasi
                                                </span>
                                            )}
                                        </td>
                                        {meeting?.sessions?.map(session => {
                                            const att = p.session_attendances?.find(a => a.session === session.id) || { status: 'pending', remarks: '' };
                                            return (
                                                <td key={session.id} className="p-5 text-center border-x border-gray-50/50">
                                                    <div className="flex flex-col gap-2">
                                                        <div className="flex items-center justify-center gap-1">
                                                            <button
                                                                onClick={() => handleAttendanceChange(p.id, 'present', att.remarks, session.id)}
                                                                className={`w-full py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition ${att.status === 'present' ? 'bg-green-600 text-white shadow-md' : 'bg-gray-100 text-gray-400 hover:bg-green-100 hover:text-green-600'}`}
                                                            >
                                                                Hadir
                                                            </button>
                                                            <button
                                                                onClick={() => handleAttendanceChange(p.id, 'absent', att.remarks, session.id)}
                                                                className={`w-full py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition ${att.status === 'absent' ? 'bg-red-600 text-white shadow-md' : 'bg-gray-100 text-gray-400 hover:bg-red-100 hover:text-red-600'}`}
                                                            >
                                                                Absen
                                                            </button>
                                                        </div>
                                                        <input 
                                                            type="text" 
                                                            value={att.remarks || ''}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                setParticipants(prev => prev.map(x => {
                                                                    if (x.id !== p.id) return x;
                                                                    const existingAtts = x.session_attendances || [];
                                                                    let newAtts;
                                                                    if (existingAtts.find(a => a.session === session.id)) {
                                                                        newAtts = existingAtts.map(a => a.session === session.id ? { ...a, remarks: val } : a);
                                                                    } else {
                                                                        newAtts = [...existingAtts, { session: session.id, status: 'pending', remarks: val }];
                                                                    }
                                                                    return { ...x, session_attendances: newAtts };
                                                                }));
                                                            }}
                                                            onBlur={(e) => handleAttendanceChange(p.id, att.status, e.target.value, session.id)}
                                                            placeholder="Catatan..."
                                                            className="w-full bg-gray-50 border-none rounded-lg px-2 py-1 text-[10px] font-medium focus:ring-1 focus:ring-blue-500"
                                                        />
                                                    </div>
                                                </td>
                                            );
                                        })}

                                    </tr>
                                ))}
                                {participants.length === 0 && (
                                    <tr>
                                        <td colSpan={4 + (meeting?.sessions?.length || 0)} className="p-12 text-center">
                                            <span className="material-icons text-gray-200 text-4xl mb-2">person_off</span>
                                            <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">Belum ada peserta terdaftar</p>
                                            <button onClick={() => setShowAddModal(true)} className="mt-4 text-blue-600 font-black text-[10px] uppercase tracking-widest hover:underline">Tambah Peserta Sekarang</button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>

            {/* MODAL: TAMBAH PESERTA */}
            {showAddModal && (
                <div className="fixed top-0 left-0 w-screen h-[100dvh] z-[1000] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setShowAddModal(false)}></div>
                    <div className="relative bg-white w-[90%] md:w-full max-w-xl rounded-[2.5rem] shadow-2xl overflow-hidden animate-fade-in flex flex-col max-h-[90vh] md:max-h-[80vh]">
                        <div className="p-5 md:p-6 border-b border-gray-100 flex items-center justify-between shrink-0">
                            <h3 className="text-lg md:text-xl font-black text-gray-900 truncate pr-4">Tambah Peserta</h3>
                            <button onClick={() => setShowAddModal(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400"><span className="material-icons text-sm">close</span></button>
                        </div>
                        <div className="p-6 flex-1 overflow-y-auto">
                            <div className="relative mb-6">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 material-icons text-gray-400">search</span>
                                <input 
                                    type="text" 
                                    placeholder="Cari user berdasarkan nama atau email..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border-none rounded-2xl text-sm focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div className="space-y-2">
                                {searching ? (
                                    <div className="text-center py-8"><span className="material-icons animate-spin text-blue-600">sync</span></div>
                                ) : users.map(u => (
                                    <div 
                                        key={u.id} 
                                        onClick={() => setSelectedUserIds(prev => prev.includes(u.id) ? prev.filter(x => x !== u.id) : [...prev, u.id])}
                                        className={`flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition ${selectedUserIds.includes(u.id) ? 'bg-blue-600 text-white' : 'bg-gray-50 hover:bg-gray-100'}`}
                                    >
                                        <div className="w-4 h-4 shrink-0 rounded border border-gray-300 flex items-center justify-center bg-white">
                                            {selectedUserIds.includes(u.id) && <span className="material-icons text-blue-600 text-xs">check</span>}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-black truncate ${selectedUserIds.includes(u.id) ? 'text-white' : 'text-gray-900'}`}>{u.profile?.name_full || u.username}</p>
                                            <p className={`text-[10px] truncate ${selectedUserIds.includes(u.id) ? 'text-blue-100' : 'text-gray-400'}`}>{u.email}</p>
                                        </div>
                                    </div>
                                ))}
                                {searchQuery && !searching && users.length === 0 && (
                                    <div className="text-center py-8 text-gray-400 text-xs font-bold uppercase tracking-widest">Tidak ada user ditemukan</div>
                                )}
                            </div>
                        </div>
                        <div className="p-6 bg-gray-50 border-t border-gray-100 shrink-0">
                            <button 
                                onClick={handleAddParticipants}
                                disabled={selectedUserIds.length === 0}
                                className={`w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl transition-all ${selectedUserIds.length > 0 ? 'bg-blue-600 text-white shadow-blue-100' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                            >
                                TAMBAHKAN ({selectedUserIds.length}) PESERTA
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: BLAST WHATSAPP */}
            {showBlastModal && (
                <div className="fixed top-0 left-0 w-screen h-[100dvh] z-[1000] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setShowBlastModal(false)}></div>
                    <div className="relative bg-white w-[90%] md:w-full max-w-xl rounded-[2.5rem] shadow-2xl overflow-hidden animate-fade-in flex flex-col max-h-[90vh] md:max-h-[80vh]">
                        <div className="p-5 md:p-6 border-b border-gray-100 flex items-center justify-between shrink-0">
                            <h3 className="text-lg md:text-xl font-black text-gray-900 truncate pr-4">Blast WhatsApp</h3>
                            <button onClick={() => setShowBlastModal(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400"><span className="material-icons text-sm">close</span></button>
                        </div>
                        <div className="p-6 md:p-8 space-y-6 overflow-y-auto">
                            <div className="bg-amber-50 border border-amber-100 p-4 rounded-2xl flex items-start gap-3">
                                <span className="material-icons text-amber-600 text-sm">info</span>
                                <p className="text-[11px] text-amber-700 leading-relaxed font-medium">Pesan ini akan dikirimkan ke <b>{selectedIds.length > 0 ? selectedIds.length : 'Semua'} peserta</b> yang memiliki nomor WhatsApp terdaftar. Gunakan bahasa yang sopan.</p>
                            </div>
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between mb-2 px-1">
                                    <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Isi Pesan Blast</label>
                                    <div className="flex flex-wrap gap-1.5 justify-end">
                                        {[
                                            { p: 'name', color: 'emerald' },
                                            { p: 'meeting_title', color: 'blue' },
                                            { p: 'time', color: 'indigo' },
                                            { p: 'location', color: 'orange' },
                                            { p: 'meeting_link', color: 'purple' }
                                        ].map(item => (
                                            <button 
                                                key={item.p}
                                                onClick={() => setBlastMessage(prev => prev + ` {${item.p}}`)}
                                                className={`text-[9px] font-bold text-${item.color}-700 bg-${item.color}-50 px-2 py-1 rounded-lg hover:bg-${item.color}-100 transition border border-${item.color}-100`}
                                            >
                                                +{'{'}{item.p}{'}'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <textarea
                                    rows="10"
                                    value={blastMessage}
                                    onChange={(e) => setBlastMessage(e.target.value)}
                                    className="w-full px-5 py-4 bg-gray-50 border-none rounded-2xl text-sm focus:ring-2 focus:ring-green-500 transition leading-relaxed"
                                    placeholder="Tulis pesan Anda..."
                                ></textarea>
                            </div>
                        </div>
                        <div className="p-6 md:p-8 bg-gray-50 border-t border-gray-100 shrink-0">
                            <button 
                                onClick={handleBlast}
                                disabled={blasting || !blastMessage}
                                className={`w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl transition-all flex items-center justify-center gap-2 ${!blasting && blastMessage ? 'bg-green-600 text-white shadow-green-100' : 'bg-gray-200 text-gray-400'}`}
                            >
                                {blasting ? <><span className="material-icons animate-spin">sync</span> MENGIRIM...</> : <><span className="material-icons text-sm">send</span> KIRIM SEKARANG</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <NavigationButton />
        </div>
    );
};

export default MeetingManagementPage;
